import type {
  ClientMeasurementId,
  ClientMeasurementValue,
  MeasurementProfileId,
  PatternDefinition,
} from "./types.js";
import { clientMeasurementRows } from "./rows.js";
import { measurementProfiles } from "./profiles.js";
import { blockMeasurementAliases } from "./aliases.js";

/**
 * Create default client measurements with empty values
 */
export const defaultClientMeasurements: Record<
  ClientMeasurementId,
  ClientMeasurementValue
> = Object.fromEntries(
  clientMeasurementRows.map((row) => [row.id, { notes: "" }]),
) as Record<ClientMeasurementId, ClientMeasurementValue>;

/**
 * Get default measurements for a pattern definition
 */
export function defaultMeasurements(
  definition: PatternDefinition,
): Record<string, number> {
  return Object.fromEntries(
    Object.entries(definition.measurements).map(([id, config]) => [
      id,
      config.defaultValue,
    ]),
  );
}

/**
 * Convert a measurement profile to client measurements
 */
export function measurementsFromProfile(
  profileId: MeasurementProfileId,
): Record<ClientMeasurementId, ClientMeasurementValue> {
  const profile = measurementProfiles[profileId];
  return {
    ...defaultClientMeasurements,
    ...Object.fromEntries(
      Object.entries(profile.values).map(([id, valueCm]) => [
        id,
        { valueCm, notes: "" },
      ]),
    ),
  } as Record<ClientMeasurementId, ClientMeasurementValue>;
}

/**
 * Check if a profile matches the given measurements
 */
export function profileMatchesMeasurements(
  profileId: MeasurementProfileId,
  measurements: Record<ClientMeasurementId, ClientMeasurementValue>,
): boolean {
  const profile = measurementsFromProfile(profileId);
  return clientMeasurementRows.every(
    (row) => measurements[row.id].valueCm === profile[row.id].valueCm,
  );
}

/**
 * Find the profile ID that matches the given measurements
 */
export function profileIdForMeasurements(
  measurements: Record<ClientMeasurementId, ClientMeasurementValue>,
  profileOrder: MeasurementProfileId[],
): MeasurementProfileId | undefined {
  return profileOrder.find((profileId) =>
    profileMatchesMeasurements(profileId, measurements),
  );
}

/**
 * Apply all client measurements to a pattern definition
 */
export function applyClientMeasurements(
  baseMeasurements: Record<string, number>,
  definition: PatternDefinition,
  clientMeasurements: Record<ClientMeasurementId, ClientMeasurementValue>,
): Record<string, number> {
  return clientMeasurementRows.reduce(
    (current, row) =>
      applyClientMeasurementValue(
        current,
        definition,
        row.id,
        clientMeasurements[row.id].valueCm,
      ),
    baseMeasurements,
  );
}

/**
 * Apply a single client measurement value to pattern measurements
 */
export function applyClientMeasurementValue(
  measurements: Record<string, number>,
  definition: PatternDefinition,
  clientMeasurementId: ClientMeasurementId,
  valueCm: number | undefined,
): Record<string, number> {
  if (valueCm === undefined || Number.isNaN(valueCm)) {
    return measurements;
  }

  const tShirtBlockIds = new Set(["flat-t-shirt", "flat-t-shirt-proper"]);
  const aliases =
    clientMeasurementId === "shortOnePieceSleeveLength" &&
    !tShirtBlockIds.has(definition.id)
      ? []
      : blockMeasurementAliases[clientMeasurementId] ?? [];
  const nextMeasurements = { ...measurements };
  aliases.forEach((measurementId) => {
    const config = definition.measurements[measurementId];
    if (!config) {
      return;
    }

    const valueMm = valueCm * 10;
    nextMeasurements[measurementId] = Math.max(
      config.min,
      Math.min(config.max, valueMm),
    );
  });
  return nextMeasurements;
}
